import { createContext, ReactNode, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  BOULEVARD_AUTH_API_TOKEN,
  CUSTOMER_ACCESS_TOKEN,
  IS_BOULEVARD_CLIENT,
} from '../../constants/Helpers';
import {
  CUSTOMER_SESSION_TOKEN,
  INTAKE_WELCOME_HOME,
} from '../../constants/intakeConstants';
import { IntakeSectionJsonTypes } from '../../types/treatmentRecord/question';
import { UserDetails } from '../../types/userTypes';
import { getJsonData } from './../../utils/helper-functions/common';

interface ICustomerContextAction {
  type: string;
  payload: string;
}

export type S3JSONTypes = { [key: string]: IntakeSectionJsonTypes };

interface ICustomerDetailsContext {
  customerAccessToken: string;
  customerSessionToken: string;
  boulevardAuthApiToken: string;
  isBoulevardClient: string;
  errorQuestionData: string[];
  progressValue: number;
  customerData: UserDetails;
  s3JsonData: S3JSONTypes;
  allAnswers: { [key: string]: string };
  allowSubmit: boolean;
  updateState: (action: ICustomerContextAction) => void;
  updateData: (_: Partial<UserDetails>) => void;
  userLogout: () => void;
  updateProgressData: (value: number) => void;
  updateError: (value: string[]) => void;
  updateSubmit: (value: boolean) => void;
}

const initialCustomerData = {
  email: '',
  userInfo: {
    id: '',
    boulevard_id: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    is_member: false,
    membership_start_on: '',
    is_intake_submitted: false,
    clientId: '',
  },
  token: '',
  isLoggedIn: false,
  section: [],
  data: [],
};

export const CustomerDetailsContext = createContext<ICustomerDetailsContext>({
  customerAccessToken: '',
  customerSessionToken: '',
  boulevardAuthApiToken: '',
  isBoulevardClient: '',
  progressValue: 5,
  s3JsonData: {},
  errorQuestionData: [],
  allAnswers: {},
  customerData: initialCustomerData,
  allowSubmit: true,
  updateData: () => {
    // this is intentionally
  },
  userLogout: () => {
    //this is intentionally
  },
  updateState: () => {
    //this is intentionally
  },
  updateProgressData: () => {
    //this is intentionally
  },
  updateError: () => {
    //this is intentionally
  },
  updateSubmit: () => {
    //this is intentionally
  },
});

const CustomerDetailsProvider = ({ children }: { children: ReactNode }) => {
  const history = useHistory();
  
  const [customerAccessToken, setCustomerAccessToken] = useState<string>('');
  const [allAnswers, setAllAnswers] = useState<{ [key: string]: string }>({});
  const [s3JsonData, setS3JsonData] = useState<S3JSONTypes>({});
  const [allowSubmit, setAllowSubmit] = useState<boolean>(true);
  const [customerSessionToken, setCustomerSessionToken] = useState<string>('');
  const [errorQuestionData, setErrorQuestionData] = useState<string[]>([]);
  const [boulevardAuthApiToken, setBoulevardAuthApiToken] =
    useState<string>('');
  const [isBoulevardClient, setIsBoulevardClient] = useState<string>('');
  const [progressValue, setProgressValue] = useState<number>(5);
  const [customerData, setCustomerData] =
    useState<UserDetails>(initialCustomerData);

  useEffect(() => {
    getJsonData(setAllAnswers, setS3JsonData);
  }, []);

  const updateData = (appStateUpdate: Partial<UserDetails>) => {
    const newData = appStateUpdate?.data ?? customerData.data;
    setCustomerData((prev) => ({ ...prev, ...appStateUpdate, data: newData }));
  };

  const updateProgressData = (value: number) => {
    setProgressValue(value);
  };

  const userLogout = () => {
    history.push(INTAKE_WELCOME_HOME);
    updateData(initialCustomerData);
  };

  const updateError = (a: string[]) => {
    setErrorQuestionData(a);
  };
  
  const updateSubmit = (a: boolean) => {
    setAllowSubmit(a);
  };
  
  useEffect(() => {
    updateData({ isLoggedIn: !!customerData.token });
  }, [customerData.token]);

  const updateState = (action: ICustomerContextAction) => {
    switch (action.type) {
      case CUSTOMER_ACCESS_TOKEN:
        setCustomerAccessToken(action.payload);
        break;
      case CUSTOMER_SESSION_TOKEN:
        setCustomerSessionToken(action.payload);
        break;
      case BOULEVARD_AUTH_API_TOKEN:
        setBoulevardAuthApiToken(action.payload);
        break;
      case IS_BOULEVARD_CLIENT:
        setIsBoulevardClient(action.payload);
        break;
    }
  };

  const value = useMemo(() => {
    return {
      allowSubmit,
      customerAccessToken,
      customerSessionToken,
      boulevardAuthApiToken,
      isBoulevardClient,
      progressValue,
      customerData,
      s3JsonData,
      errorQuestionData,
      allAnswers,
      updateState,
      updateData,
      userLogout,
      updateError,
      updateProgressData,
      updateSubmit,
    };
  }, [
    allowSubmit,
    customerAccessToken,
    customerSessionToken,
    boulevardAuthApiToken,
    isBoulevardClient,
    progressValue,
    customerData,
    s3JsonData,
    errorQuestionData,
    allAnswers,
    updateState,
    updateData,
    userLogout,
    updateError,
    updateProgressData,
    updateSubmit,
  ]);

  return (
    <CustomerDetailsContext.Provider value={value}>
      {children}
    </CustomerDetailsContext.Provider>
  );
};

export default CustomerDetailsProvider;
